import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { InvoiceInterface } from '@app/interface/invoice';
import { InvoiceApi } from '@app/services/invoice-api';
import { MatChipsModule } from '@angular/material/chips';

export interface CompanyInvoicesData {
  company_id: number;
  companyName: string;
  organizationId: number;
}

@Component({
  selector: 'izy-company-invoices-bottom-sheet',
  imports: [
    CommonModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatChipsModule,
  ],
  standalone: true,
  templateUrl: './company-invoices-bottom-sheet.component.html',
  styleUrl: './company-invoices-bottom-sheet.component.scss',
})
export class CompanyInvoicesBottomSheetComponent implements OnInit {
  invoices: InvoiceInterface[] = [];
  isLoading = true;
  errorMessage: string | null = null;

  constructor(
    private bottomSheetRef: MatBottomSheetRef<CompanyInvoicesBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: CompanyInvoicesData,
    private invoiceService: InvoiceApi,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadInvoices();
  }

  public loadInvoices() {
    this.isLoading = true;
    this.errorMessage = null;

    // Filter invoices by company ID
    const filterParams = {
      company: this.data.company_id,
      is_deleted: false, // Only show non-deleted invoices
      is_draft: false,
    };

    this.invoiceService
      .getAllInvoices<InvoiceInterface>(this.data.organizationId, filterParams)
      .subscribe({
        next: (response) => {
          if (response.completed) {
            this.invoices = response.results.sort((a, b) => {
              return b.progressive_number - a.progressive_number;
            });

            this.invoices = response.results;
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading invoices:', err);
          this.errorMessage = 'Errore nel caricamento delle fatture';
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  navigateToInvoice(invoice: InvoiceInterface) {
    const orgId = this.data.organizationId;
    this.router.navigate([`/${orgId}/invoice`, invoice.id]);
    this.bottomSheetRef.dismiss();
  }

  navigateToNewInvoice() {
    const orgId = this.data.organizationId;
    this.router.navigate([`/${orgId}/new_invoice`], {
      queryParams: {
        companyId: this.data.company_id,
        companyName: this.data.companyName,
      },
    });
    this.bottomSheetRef.dismiss();
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('it-IT');
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }

  getInvoiceStatus(invoice: InvoiceInterface): string {
    if (invoice.is_draft) return 'Bozza';
    if (invoice.is_paid) return 'Incassata';
    return 'Da pagare';
  }

  getStatusColor(invoice: InvoiceInterface): string {
    if (invoice.is_draft) return 'accent';
    if (invoice.is_paid) return 'primary';
    return 'warn';
  }

  closeSheet(): void {
    this.bottomSheetRef.dismiss();
  }
}
